import { useState, useEffect, useMemo, useContext } from "react";
import { toast } from "react-toastify";
import { useParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import Autocomplete from "@mui/material/Autocomplete";
import { CustomDatePicker } from "../UI/CustomDatePicker";

import TrialParticipantIdAccordion from "../TrialAccordions/TrialParticipantIdAccordion";
import InvestigatorSiteIdAccordion from "../TrialAccordions/InvestigatorSiteIdAccordion";
import TrialReviewersAccordion from "../TrialAccordions/TrialReviewersAccordion";
import MaximumPhotoTypesAccordion from "../TrialAccordions/MaximumPhotoTypesAccordion";
import MaximumVideoTypesAccordion from "../TrialAccordions/MaximumVideoTypesAccordion";
import VideoLengthAccordion from "../TrialAccordions/VideoLengthAccordion";
import { LoaderContext } from "../../context/LoaderContext";
import { __API_URL__ } from "../../SERVER_URL";
import useUser from "../../hooks/user";
import { useEcrfContext } from "../../context/EcrfContext";
import useGetTrialTypes from "../../hooks/useGetTrialTypes";
import EConsentAccordion from "../TrialAccordions/EConsentAccordion";
import { USER_ROLES } from "../../utils/constants";
import AppActivitiesOrderAccordian from "../TrialAccordions/AppActivitiesOrderAccordian";
import useGetSessionTrialConfig from "../../hooks/useGetSessionTrialConfig";
import useUpdateSessionTrialConfigMutation from "../../hooks/useUpdateSessionTrialConfigMutation";
import useGetNewEcrfs from "../../hooks/useGetNewEcrfs";
import { DEFAULT_EDIARY_CONFIG } from "./EDiaryConfigSection";
import { isTrialConfigFinalized } from "../../utils/helpers";

const SESSION_BASED_ALLOWED_OPTIONS = [
  "blur_image_background",
  "individual_eye_block",
  "make_translucent_image",
  "has_ai_evaluation",
  "add_censor_bar",
  "show_ai_evaluation",
  "show_experience",
  "show_profile",
  "show_econsent",
  "show_digital_consent",
  "show_practitioner",
  "show_rewards",
  "show_onboarding_screens",
  "show_survey_histogram",
];

const APP_RELATED_OPTIONS = [
  "is_maximum_photo_types",
  "is_maximum_video_types",
  "blur_image_background",
  "add_censor_bar",
  "individual_eye_block",
  "make_translucent_image",
  "has_ai_evaluation",
  "show_ai_evaluation",
  "show_experience",
  "show_profile",
  "show_econsent",
  "show_digital_consent",
  "show_practitioner",
  "show_rewards",
  "show_onboarding_screens",
  "enable_app_documents",
  "show_survey_histogram",
];

const BasicInfo = ({ isEvent, setisEvent, setSelectedTrialType }) => {
  const token = localStorage.getItem("token");
  const user_data = JSON.parse(localStorage.getItem("user_data"));
  const role = user_data.role;
  const user = useUser();
  const loaderContext = useContext(LoaderContext);
  const queryClient = useQueryClient();

  const { eventList, setEventList, setTrial, setTrialTypes, trial } = useEcrfContext();

  const isTrialLocked = ["Completed", "Archived"].includes(trial?.status);
  const isFinalized = isTrialConfigFinalized(trial);

  const { _id_ } = useParams();
  const [trialDetails, setTrialDetails] = useState({});
  const [allInvestigators, setAllInvestigators] = useState([]);
  const [allReviewers, setAllReviewers] = useState([]);
  const [modalTrialTypeShow, setModalTrialTypeShow] = useState(false);
  const [changeTrialSetting, setChangeTrialSetting] = useState({
    participant_ids: "",
    site_ids: "",
  });
  const [changeSetting, setChangeSetting] = useState(false);
  const [siteIdManual, setSiteIdManual] = useState(false);
  const [investigatorChanged, setInvestigatorChanged] = useState(false);
  const [trialSetting, setTrialSetting] = useState("Participant Id");
  const [infoSettingChanged, setInfoSettingChanged] = useState(false);
  const [reviewerChanged, setReviewerChanged] = useState(false);
  const [ParticipantIdChanged, setParticipantIdChanged] = useState(false);
  const [maximumPhototypes, setMaximumPhototypes] = useState(0);
  const [maximumVideotypes, setMaximumVideotypes] = useState(0);
  const [videoLength, setVideoLength] = useState(0);
  const [errorMsg, seterrorMsg] = useState("");
  const [title, setTitle] = useState("");
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [isMounted, setIsMounted] = useState(false);
  const [hasBasicInfoChanged, setHasBasicInfoChanged] = useState(false);
  const [maxSessions, setMaxSessions] = useState("");
  const [sessionConfigChanged, setSessionConfigChanged] = useState(false);

  const options = useMemo(
    () => [
      {
        value: "screening_visit",
        label: "Screening Visit",
        description: "Adds a Screening Visit step before the regular visit schedule for patients.",
        isDisabled: !["CRO Admin", "Cro_coordinator", "Data Manager", "Super Admin"].includes(role),
      },
      {
        value: "unscheduled_event",
        label: "Unscheduled Visit",
        description:
          "Lets staff log a unscheduled visit for a patient outside the normal visit schedule.",
        isDisabled: !["CRO Admin", "Cro_coordinator", "Data Manager", "Super Admin"].includes(role),
      },
      {
        value: "adverse_event",
        label: "Adverse Event",
        description:
          "Lets staff record an Adverse Event visit for a patient; completing it emails the investigator and coordinators.",
        isDisabled: !["CRO Admin", "Cro_coordinator", "Data Manager", "Super Admin"].includes(role),
      },
      {
        value: "ediary",
        label: "eDiary",
        description:
          "Adds a recurring survey diary that is scheduled automatically for every subject. After saving, configure it in the eDiary tab of Trial Visit & Source Document.",
        isDisabled: !["CRO Admin", "Cro_coordinator", "Data Manager", "Super Admin"].includes(role),
      },
      {
        value: "is_maximum_photo_types",
        label: "Maximum App Photos Per Subject",
        description:
          "Caps how many photo types an investigator can upload per visit and blocks saving beyond that limit.",
        isDisabled: ["Investigator", "Inv_coordinator"].includes(role),
      },
      {
        value: "is_maximum_video_types",
        label: "Maximum App Videos Per Subject",
        description:
          "Caps how many video types an investigator can upload per visit and blocks saving beyond that limit.",
        isDisabled: ["Investigator", "Inv_coordinator"].includes(role),
      },
      {
        value: "allow_concurrent_enrollment",
        label: "Allow Concurrent Enrollment",
        description:
          "Allows a patient to enroll in any other trial while already actively enrolled in this trial.",
        isDisabled: !["CRO Admin", "Cro_coordinator", "Data Manager"].includes(role),
      },
      {
        value: "enforce_visit_start_time_at_12",
        label: "Enforce Visit Start Time at Midnight",
        description:
          "Forces visit start time to midnight. When off, staff can pick a full date and time.",
        isDisabled: !["CRO Admin", "Cro_coordinator", "Data Manager"].includes(role),
      },
      {
        value: "is_hide_image",
        label: "Hide Subject Photos for CRO",
        description:
          "Hides subject photos (in viewers, reports, and thumbnails) from CRO Admin, Coordinator, and Data Manager roles.",
        isDisabled: !["CRO Admin", "Cro_coordinator", "Data Manager"].includes(role),
      },
      {
        value: "can_edit_instructions",
        label: "INV Subject Instruction Edit",
        description:
          "Allows investigators and coordinators to edit the subject visit instructions text.",
        isDisabled: !["CRO Admin", "Cro_coordinator", "Data Manager"].includes(role),
      },
      {
        value: "blur_image_background",
        label: "Blur Image Background (App Photo)",
        description:
          "Blurs everything outside a center circle in app photos, keeping only the central subject sharp.",
        isDisabled: !["CRO Admin", "Cro_coordinator", "Data Manager"].includes(role),
      },
      {
        value: "add_censor_bar",
        label: "Add Censor Bar (App Photo)",
        description:
          "Draws a black bar across detected eyes in app photos to anonymize the patient. Mutually exclusive with Individual Eye Block.",
        isDisabled: !["CRO Admin", "Cro_coordinator", "Data Manager"].includes(role),
      },
      {
        value: "individual_eye_block",
        label: "Individual Eye Block (App Photo)",
        description:
          "Draws two separate black rectangles over each detected eye in app photos. Mutually exclusive with Add Censor Bar.",
        isDisabled: !["CRO Admin", "Cro_coordinator", "Data Manager"].includes(role),
      },
      {
        value: "make_translucent_image",
        label: "Make Translucent Image (App Photo)",
        description:
          "Generates a see-through baseline overlay from a prior photo so patients can align their next photo to it.",
        isDisabled: !["CRO Admin", "Cro_coordinator", "Data Manager"].includes(role),
      },
      {
        value: "show_external_photos",
        label: "Show External Photos",
        description:
          "Shows the External Photos tab (and logs, for admin roles) on a patient's profile.",
        isDisabled: !["CRO Admin", "Cro_coordinator", "Data Manager"].includes(role),
      },
      {
        value: "show_external_videos",
        label: "Show External Videos",
        description:
          "Shows the External Videos tab (and logs, for admin roles) on a patient's profile.",
        isDisabled: !["CRO Admin", "Cro_coordinator", "Data Manager"].includes(role),
      },
      {
        value: "show_external_document",
        label: "Show External Document",
        description: "Shows the External Documents tab on a patient's profile.",
        isDisabled: !["CRO Admin", "Cro_coordinator", "Data Manager"].includes(role),
      },
      {
        value: "is_hide_video",
        label: "Hide Subject Videos for CRO",
        description:
          "Hides recorded visit videos from CRO Admin, Coordinator, and Data Manager roles.",
        isDisabled: !["CRO Admin", "Cro_coordinator", "Data Manager"].includes(role),
      },
      {
        value: "ecrf_password_authentication",
        label: "Investigator Signature Authentication",
        description:
          "Requires an e-signature/password confirmation when certifying or un-certifying an eCRF block.",
        isDisabled: !["CRO Admin", "Cro_coordinator", "Data Manager"].includes(role),
      },
      {
        value: "has_ai_evaluation",
        label: "AI Evaluation",
        description: "Master switch for enabling AI Assessment.",
        isDisabled: !["CRO Admin", "Cro_coordinator", "Data Manager"].includes(role),
      },
      {
        value: "show_ai_evaluation",
        label: "Show AI Evaluation (App)",
        description:
          "Shows AI evaluation results in the app session; if off while AI Evaluation is on, AI still runs but results stay hidden.",
        isDisabled: !["CRO Admin", "Cro_coordinator", "Data Manager"].includes(role),
      },
      {
        value: "show_experience",
        label: "Show Experience (App)",
        description:
          "Shows an experience/treatment history step in the app; for non-session trials it must be filled in before a visit auto-completes.",
        isDisabled: !["CRO Admin", "Cro_coordinator", "Data Manager"].includes(role),
      },
      {
        value: "show_profile",
        label: "Show Profile (App)",
        description:
          "Shows a profile step in the app; for non-session trials it must be filled in before a visit auto-completes.",
        isDisabled: !["CRO Admin", "Cro_coordinator", "Data Manager"].includes(role),
      },
      {
        value: "show_econsent",
        label: "Show eConsent (App)",
        description:
          "Enables the electronic consent flow in the app. Mutually exclusive with Show Digital Consent.",
        isDisabled: !["CRO Admin", "Cro_coordinator", "Data Manager"].includes(role),
      },
      {
        value: "show_digital_consent",
        label: "Show Digital Consent (App)",
        description:
          "Enables signing the consent form digitally via DocuSeal. Mutually exclusive with Show eConsent.",
        isDisabled: !["CRO Admin", "Cro_coordinator", "Data Manager"].includes(role),
      },
      {
        value: "enforce_visit_protocol",
        label: "Enforce Visit Protocol",
        description:
          "Locks visit dates to the trial protocol; when off, staff can manually adjust a patient's visit dates.",
        isDisabled: !["CRO Admin", "Cro_coordinator", "Data Manager"].includes(role),
      },
      {
        value: "show_practitioner",
        label: "Show Practitioner (App)",
        description: "Shows the practitioner's name and info to the patient in the mobile app.",
        isDisabled: !["CRO Admin", "Cro_coordinator", "Data Manager"].includes(role),
      },
      {
        value: "show_rewards",
        label: "Show Rewards (App)",
        description: "Shows the rewards feature/section to the patient in the mobile app.",
        isDisabled: !["CRO Admin", "Cro_coordinator", "Data Manager"].includes(role),
      },
      {
        value: "show_onboarding_screens",
        label: "Show Onboarding Screens (App)",
        description: "Shows the onboarding screens to the patient in the mobile app.",
        isDisabled: !["CRO Admin", "Cro_coordinator", "Data Manager"].includes(role),
      },
      {
        value: "enable_app_documents",
        label: "Enable App Documents (App)",
        description:
          "Lets patients view and access trial-related documents from within the mobile app.",
        isDisabled: !["CRO Admin", "Cro_coordinator", "Data Manager"].includes(role),
      },
      {
        value: "show_survey_histogram",
        label: "Show Survey Histogram (App)",
        description:
          "Shows a Histogram view on the trial and patient pages with aggregated ecrf and survey response data.",
        isDisabled: !["CRO Admin", "Cro_coordinator", "Data Manager"].includes(role),
      },
      {
        value: "show_incomplete_date_warning_on_ecrf",
        label: "Show Incomplete Date Warning on eCRF",
        description:
          "Shows a reminder popup on the eCRF when an in-clinic visit's start/completion date is missing.",
        isDisabled: !["CRO Admin", "Cro_coordinator", "Data Manager"].includes(role),
      },
      {
        value: "auto_forward_support_to_site_staff",
        label: "Auto-Forward Support Messages to Site Staff",
        description:
          "Automatically adds the patient's Investigator, CRC and Sub-Investigator to their support conversations, without a Super Admin forwarding them.",
        isDisabled: !["CRO Admin", "Cro_coordinator", "Data Manager"].includes(role),
      },
    ],
    [role]
  );

  const sessionOptions = useMemo(
    () => options.filter((o) => SESSION_BASED_ALLOWED_OPTIONS.includes(o.value)),
    [options]
  );

  const isSessionBasedTrial = trialDetails.trial_structure === "session_based";
  const visibleOptions = isSessionBasedTrial ? sessionOptions : options;

  const appOptions = useMemo(
    () => visibleOptions.filter((o) => APP_RELATED_OPTIONS.includes(o.value)),
    [visibleOptions]
  );
  const dashboardOptions = useMemo(
    () => visibleOptions.filter((o) => !APP_RELATED_OPTIONS.includes(o.value)),
    [visibleOptions]
  );
  const allDashboardOptionValues = useMemo(
    () => options.filter((o) => !APP_RELATED_OPTIONS.includes(o.value)).map((o) => o.value),
    [options]
  );

  const optionsFunctionsMapping = {
    allow_concurrent_enrollment: () => {
      setTrialDetails((prev) => ({
        ...prev,
        allow_concurrent_enrollment: !prev.allow_concurrent_enrollment,
      }));
    },
    enforce_visit_start_time_at_12: () => {
      setTrialDetails((prev) => ({
        ...prev,
        enforce_visit_start_time_at_12: !prev.enforce_visit_start_time_at_12,
      }));
    },
    individual_eye_block: () => {
      setTrialDetails((prev) => {
        const updatedValue = !prev.individual_eye_block;
        return {
          ...prev,
          individual_eye_block: updatedValue,
          add_censor_bar: updatedValue ? false : prev.add_censor_bar,
        };
      });
    },
    add_censor_bar: () => {
      setTrialDetails((prev) => {
        const updatedValue = !prev.add_censor_bar;
        return {
          ...prev,
          add_censor_bar: updatedValue,
          individual_eye_block: updatedValue ? false : prev.individual_eye_block,
        };
      });
    },
    blur_image_background: () => {
      setTrialDetails((prev) => ({
        ...prev,
        blur_image_background: !prev.blur_image_background,
      }));
    },
    make_translucent_image: () => {
      setTrialDetails((prev) => ({
        ...prev,
        make_translucent_image: !prev.make_translucent_image,
      }));
    },
    has_ai_evaluation: () => {
      setTrialDetails((prev) => ({
        ...prev,
        has_ai_evaluation: !prev.has_ai_evaluation,
      }));
    },
    show_ai_evaluation: () => {
      setTrialDetails((prev) => ({
        ...prev,
        show_ai_evaluation: !prev.show_ai_evaluation,
      }));
    },
    show_experience: () => {
      setTrialDetails((prev) => ({
        ...prev,
        show_experience: !prev.show_experience,
      }));
    },
    enable_app_documents: () => {
      setTrialDetails((prev) => ({
        ...prev,
        enable_app_documents: !prev.enable_app_documents,
      }));
    },
    show_survey_histogram: () => {
      setTrialDetails((prev) => ({
        ...prev,
        show_survey_histogram: !prev.show_survey_histogram,
      }));
    },
    show_incomplete_date_warning_on_ecrf: () => {
      setTrialDetails((prev) => ({
        ...prev,
        show_incomplete_date_warning_on_ecrf: !prev.show_incomplete_date_warning_on_ecrf,
      }));
    },
    auto_forward_support_to_site_staff: () => {
      setTrialDetails((prev) => ({
        ...prev,
        auto_forward_support_to_site_staff: !prev.auto_forward_support_to_site_staff,
      }));
    },
    show_profile: () => {
      setTrialDetails((prev) => ({
        ...prev,
        show_profile: !prev.show_profile,
      }));
    },
    show_econsent: () => {
      setTrialDetails((prev) => {
        const updatedValue = !prev.show_econsent;
        return {
          ...prev,
          show_econsent: updatedValue,
          show_digital_consent: updatedValue ? false : prev.show_digital_consent,
        };
      });
    },
    show_digital_consent: () => {
      setTrialDetails((prev) => {
        const updatedValue = !prev.show_digital_consent;
        return {
          ...prev,
          show_digital_consent: updatedValue,
          show_econsent: updatedValue ? false : prev.show_econsent,
        };
      });
    },
    show_practitioner: () => {
      setTrialDetails((prev) => ({
        ...prev,
        show_practitioner: !prev.show_practitioner,
      }));
    },
    show_rewards: () => {
      setTrialDetails((prev) => ({
        ...prev,
        show_rewards: !prev.show_rewards,
      }));
    },
    show_onboarding_screens: () => {
      setTrialDetails((prev) => ({
        ...prev,
        show_onboarding_screens: !prev.show_onboarding_screens,
      }));
    },
    enforce_visit_protocol: () => {
      setTrialDetails((prev) => ({
        ...prev,
        enforce_visit_protocol: !prev.enforce_visit_protocol,
      }));
    },
    show_external_photos: () => {
      setTrialDetails((prev) => ({
        ...prev,
        show_external_photos: !prev.show_external_photos,
      }));
    },
    show_external_videos: () => {
      setTrialDetails((prev) => ({
        ...prev,
        show_external_videos: !prev.show_external_videos,
      }));
    },
    ecrf_password_authentication: () => {
      setTrialDetails((prev) => ({
        ...prev,
        ecrf_password_authentication: !prev.ecrf_password_authentication,
      }));
    },
    show_external_document: () => {
      setTrialDetails((prev) => ({
        ...prev,
        show_external_document: !prev.show_external_document,
      }));
    },
    can_edit_instructions: () => {
      setTrialDetails((prev) => ({
        ...prev,
        can_edit_instructions: !prev.can_edit_instructions,
      }));
    },
    is_hide_image: () => {
      setTrialDetails((prev) => ({
        ...prev,
        is_hide_image: !prev.is_hide_image,
      }));
    },
    is_hide_video: () => {
      setTrialDetails((prev) => ({
        ...prev,
        is_hide_video: !prev.is_hide_video,
      }));
    },
    is_maximum_photo_types: () => {
      setTrialDetails((prev) => ({
        ...prev,
        is_maximum_photo_types: !prev.is_maximum_photo_types,
      }));
    },
    is_maximum_video_types: () => {
      setTrialDetails((prev) => ({
        ...prev,
        is_maximum_video_types: !prev.is_maximum_video_types,
      }));
    },
    adverse_event: () => {
      setisEvent((prev) => ({ ...prev, adverse_event: !prev.adverse_event }));
      handleAdverseEventClick(!isEvent.adverse_event);
    },
    unscheduled_event: () => {
      setisEvent((prev) => ({
        ...prev,
        unscheduled_event: !prev.unscheduled_event,
      }));
      handleUnscheduledEventClick(!isEvent.unscheduled_event);
    },
    screening_visit: () => {
      setisEvent((prev) => ({
        ...prev,
        screening_visit: !prev.screening_visit,
      }));
      handleScreeningEventClick(!isEvent.screening_visit);
    },
    ediary: () => {
      const willEnable = !isEvent.ediary;
      setisEvent((prev) => ({ ...prev, ediary: willEnable }));
      // Turning it off keeps the loaded settings, so turning it back on before saving cannot
      // replace them with the defaults.
      if (willEnable) {
        setTrialDetails((prev) => ({
          ...prev,
          ediary_config: prev.ediary_config || { ...DEFAULT_EDIARY_CONFIG },
        }));
      }
    },
  };

  useEffect(() => {
    if (trialDetails.ediary_config) {
      setisEvent((prev) => (prev.ediary ? prev : { ...prev, ediary: true }));
    }
  }, [trialDetails.ediary_config]);

  useEffect(() => {
    if (!isMounted && trialDetails.trial_options) {
      setSelectedOptions(trialDetails.trial_options);
      setIsMounted(true);
    }
  }, [trialDetails]);

  const handleChange = (event, selected, reason, details, groupValues) => {
    if (reason === "selectOption") {
      const optionValue = details?.option?.value;

      if (optionValue === "individual_eye_block")
        selected = selected.filter((item) => item.value !== "add_censor_bar");
      else if (optionValue === "add_censor_bar")
        selected = selected.filter((item) => item.value !== "individual_eye_block");

      if (optionValue === "show_econsent")
        selected = selected.filter((item) => item.value !== "show_digital_consent");
      else if (optionValue === "show_digital_consent")
        selected = selected.filter((item) => item.value !== "show_econsent");

      const selectedFunction = optionsFunctionsMapping[optionValue];
      if (selectedFunction) {
        selectedFunction();
      }
    } else if (reason === "removeOption") {
      const removedOption = details?.option;
      if (removedOption?.isDisabled) {
        return;
      }
      const removedFunction = optionsFunctionsMapping[removedOption?.value];
      if (removedFunction) {
        removedFunction();
      }
    }

    setSelectedOptions((prev) => [
      ...prev.filter((o) => !groupValues.includes(o.value)),
      ...selected,
    ]);
    setHasBasicInfoChanged(true);
  };

  const onInvestigatorChange = (event, newValue, reason, details) => {
    switch (reason) {
      case "removeOption":
        if (details?.option?.isFixed) {
          toast.info("Cannot remove the Investigator");
          return;
        }
        setInvestigatorChanged(true);
        setChangeSetting(true);
        setTrialDetails((trial) => ({
          ...trial,
          investigators: newValue,
        }));
        break;
      case "clear":
        setInvestigatorChanged(true);
        setChangeSetting(true);
        const fixedInvestigators = trialDetails.investigators.filter((v) => v.isFixed);
        setTrialDetails((trial) => ({
          ...trial,
          investigators: fixedInvestigators,
        }));
        break;
      case "selectOption":
        setInvestigatorChanged(true);
        setChangeSetting(true);
        setTrialDetails((trial) => ({
          ...trial,
          investigators: newValue,
        }));
        break;
      default:
        break;
    }
  };

  const onReviewerChange = (event, newValue, reason, details) => {
    switch (reason) {
      case "removeOption":
        if (details?.option?.isFixed) {
          toast.warning("Cannot remove the reviewer");
          return;
        }
        setReviewerChanged(true);
        setTrialDetails((trial) => ({
          ...trial,
          reviewers: newValue,
        }));
        break;
      case "clear":
        setReviewerChanged(true);
        const fixedReviewers = trialDetails.reviewers.filter((v) => v.isFixed);
        setTrialDetails((trial) => ({
          ...trial,
          reviewers: fixedReviewers,
        }));
        break;
      case "selectOption":
        setReviewerChanged(true);
        setTrialDetails((trial) => ({
          ...trial,
          reviewers: newValue,
        }));
        break;
      default:
        break;
    }
  };

  const handleFieldChange = (e) => {
    setHasBasicInfoChanged(true);
    const { name, value } = e.target;
    setTrialDetails((trial) => {
      return {
        ...trial,
        [name]: value,
      };
    });
  };

  const handleBasicInfoUpdate = () => {
    if (trialDetails.video_length < 1 || trialDetails.video_length > 120) {
      toast.error("Range of the video duration should be between 1-30 seconds.");
      return;
    }
    // Once the eDiary exists its settings are edited and saved from the eDiary tab, so they are
    // only sent when this save is the one that creates it (with the defaults).
    const { ediary_config: ediaryConfig, ...trialFields } = trialDetails;
    const isCreatingEdiary = isEvent.ediary && !ediaryConfig?.visit_id;
    const updatedTrialDetails = {
      ...trialFields,
      special_visits: isEvent,
      ...(isCreatingEdiary ? { ediary_config: ediaryConfig } : {}),
    };

    loaderContext.setLoaderCount((prev) => prev + 1);

    axios
      .patch(`${__API_URL__}/api/photos/edit_trial/${trialDetails.id}/`, updatedTrialDetails, {
        headers: {
          Authorization: `Token ${token}`,
        },
      })
      .then((res) => {
        res = res?.data;
        let investigator_list = [];
        queryClient.invalidateQueries({ queryKey: ["visits"] });
        // trial_modules (App Activities Order) is part of this same PATCH payload, and
        // SessionActivities' enable_photos/enable_poem/etc. flags are derived from it
        // server-side — without this, adding/enabling a module here leaves Session
        // Activities showing stale (pre-update) data until an unrelated refetch happens.
        if (trialDetails.trial_structure === "session_based") {
          // Match the exact key useGetSessionTrialConfig registers below (trialId: _id_, a
          // string route param) — trialDetails.id is a number and would silently fail to match.
          queryClient.invalidateQueries({ queryKey: ["session_trial_config", _id_] });
        }
        setTrial(res);
        setEventList((prevList) =>
          res.ediary_config
            ? prevList.includes(-6)
              ? prevList
              : [...prevList, -6]
            : prevList.filter((event) => event !== -6)
        );

        res.investigator_details.forEach((investigator) => {
          investigator_list.push({
            id: investigator.id,
            value: investigator.email,
            label: investigator.email,
            isFixed: true,
            site_number: investigator.site_number,
            minimum_drug: investigator.minimum_drug,
            max_patient: investigator.max_patient,
            site_address: investigator.site_address,
            site_phone: investigator.site_phone,
          });
        });

        let reviewer_list = [];
        res.reviewer_details.forEach((reviewer) => {
          reviewer_list.push({
            value: reviewer.email,
            label: reviewer.email,
            isFixed: true,
            site_number: reviewer.site_number,
          });
        });

        res = {
          ...res,
          investigators: investigator_list,
          reviewers: reviewer_list,
        };

        setSelectedTrialType(res.trial_type?.title);
        setTrialDetails({
          ...res,
          trial_type: res.trial_type?.id,
          trial_type_title: res.trial_type?.title,
        });
        setChangeSetting(false);
        setHasBasicInfoChanged(false);
        setInfoSettingChanged(false);
        setInvestigatorChanged(false);
        setReviewerChanged(false);
        setParticipantIdChanged(false);

        if (isSessionBased && sessionConfigChanged) {
          updateSessionConfig.mutate(
            { max_sessions: maxSessions === "" ? null : Number(maxSessions) },
            {
              onSuccess: () => setSessionConfigChanged(false),
              onError: () => toast.error("Failed to update session limit"),
            }
          );
        }

        toast.success("Updated Successfully");
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || "Update failed");
      })
      .finally(() => {
        loaderContext.setLoaderCount((prev) => prev - 1);
      });
  };

  const trialDetailApi = () => {
    loaderContext.setLoaderCount((prev) => prev + 1);
    axios
      .get(`${__API_URL__}/api/photos/get_trial/${_id_}/`, {
        headers: {
          Authorization: `Token ${token}`,
        },
      })
      .then((res) => {
        res = res.data;
        let investigator_list = [];
        res.investigator_details.forEach((investigator) => {
          investigator_list.push({
            id: investigator.id,
            value: investigator.email,
            label: investigator.email,
            isFixed: true,
            site_number: investigator.site_number,
            minimum_drug: investigator.minimum_drug,
            max_patient: investigator.max_patient,
            site_address: investigator.site_address,
            site_phone: investigator.site_phone,
          });
        });
        let reviewer_list = [];
        res.reviewer_details.forEach((reviewer) => {
          reviewer_list.push({
            value: reviewer.email,
            label: reviewer.email,
            isFixed: true,
            site_number: reviewer.site_number,
          });
        });
        res = {
          ...res,
          investigators: investigator_list,
          reviewers: reviewer_list,
        };
        setTrialDetails({
          ...res,
          trial_type: res.trial_type?.id,
          trial_type_title: res.trial_type?.title,
        });
      })
      .finally(() => {
        loaderContext.setLoaderCount((prev) => prev - 1);
      });
  };

  const AllInvestigatorsData = () => {
    loaderContext.setLoaderCount((prev) => prev + 1);
    axios
      .get(`${__API_URL__}/api/investigatorsList/`, {
        headers: {
          Authorization: `Token ${token}`,
        },
      })
      .then((res) => {
        let customData = [];
        res.data.forEach((obj) => {
          customData.push({
            value: obj.email,
            label: obj.email,
          });
        });
        setAllInvestigators(customData);
      })
      .catch((err) => {
        if (err === "Error: Request failed with status code 401") {
          user.actions.logout();
        }
      })
      .finally(() => {
        loaderContext.setLoaderCount((prev) => prev - 1);
      });
  };

  const AllReviewerData = () => {
    loaderContext.setLoaderCount((prev) => prev + 1);
    axios
      .get(`${__API_URL__}/api/reviewerList/`, {
        headers: {
          Authorization: `Token ${token}`,
        },
      })
      .then((res) => {
        setAllReviewers(res.data);
      })
      .catch((err) => {
        if (err === "Error: Request failed with status code 401") {
          user.actions.logout();
        }
      })
      .finally(() => {
        loaderContext.setLoaderCount((prev) => prev - 1);
      });
  };

  const { data: trialTypesData } = useGetTrialTypes();

  const isSessionBased = trialDetails.trial_structure === "session_based";

  const { data: sessionConfigData } = useGetSessionTrialConfig({
    trialId: _id_,
    enabled: isSessionBased,
  });

  const updateSessionConfig = useUpdateSessionTrialConfigMutation({ trialId: _id_ });

  const { data: scoreEcrfOptions = [] } = useGetNewEcrfs({ listAll: true });

  useEffect(() => {
    if (sessionConfigData) {
      setMaxSessions(sessionConfigData.max_sessions ?? "");
    }
  }, [sessionConfigData]);

  useEffect(() => {
    setTrialTypes(trialTypesData);
  }, [trialTypesData]);

  useEffect(() => {
    trialDetailApi();
    if (role === USER_ROLES.SUPER_ADMIN) {
      AllInvestigatorsData();
      AllReviewerData();
    }
  }, []);

  const handleScreeningEventClick = (isChecked) => {
    if (isChecked) {
      if (!eventList?.includes(-3)) {
        setEventList((prevList) => [...prevList, -3]);
      }
    } else {
      setEventList((prevList) => prevList.filter((event) => event !== -3));
    }
  };

  const handleUnscheduledEventClick = (isChecked) => {
    if (isChecked) {
      if (!eventList?.includes(-2)) {
        setEventList((prevList) => [...prevList, -2]);
      }
    } else {
      setEventList((prevList) => prevList.filter((event) => event !== -2));
    }
  };

  const handleAdverseEventClick = (isChecked) => {
    if (isChecked) {
      if (!eventList?.includes(-1)) {
        setEventList((prevList) => [...prevList, -1]);
      }
    } else {
      setEventList((prevList) => prevList.filter((event) => event !== -1));
    }
  };

  const addTrialTypeApi = () => {
    setModalTrialTypeShow(false);
    const payload = {
      title: title,
    };

    axios
      .post(`${__API_URL__}/api/photos/add_trial_type/`, payload, {
        headers: {
          Authorization: `Token ${token}`,
        },
      })
      .then((res) => {
        const newTrialType = res.data;
        setTrialTypes((prevTrialTypes) => [...prevTrialTypes, newTrialType]);
        setTrialDetails((prevDetails) => ({
          ...prevDetails,
          trial_type: newTrialType.id,
        }));
        toast.success("Trial Type Added");
        setTitle("");
      })
      .catch((err) => {
        toast.error("Something Went Wrong!");
      });
  };

  return (
    <>
      {role !== "Investigator" && role !== "Inv_coordinator" && role !== "Reviewer" && (
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="contained"
            size="small"
            data-testid="update-basic-info-btn"
            onClick={() => handleBasicInfoUpdate()}
            disabled={!hasBasicInfoChanged || isTrialLocked}
          >
            Update Basic Info
          </Button>
        </Box>
      )}

      <Box
        key={trialDetails.id}
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
          pb: 2,
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: 2,
            py: 1,
          }}
        >
          <TextField
            fullWidth
            label="Custom Id"
            size="small"
            name="trial_custom_id"
            value={trialDetails.trial_custom_id || ""}
            disabled
          />
          <TextField
            fullWidth
            label="Name"
            size="small"
            name="name"
            value={trialDetails.name || ""}
            onChange={handleFieldChange}
          />
          <TextField
            fullWidth
            select
            label="Status"
            size="small"
            name="status"
            value={trialDetails.status || ""}
            onChange={handleFieldChange}
            SelectProps={{ SelectDisplayProps: { "data-testid": "basic-info-status-trigger" } }}
          >
            <MenuItem value="" disabled>
              Select
            </MenuItem>
            <MenuItem value="Active">Active</MenuItem>
            <MenuItem value="Completed">Completed</MenuItem>
            <MenuItem value="Archived">Archived</MenuItem>
          </TextField>

          {isSessionBased &&
            role !== "Investigator" &&
            role !== "Inv_coordinator" &&
            role !== "Reviewer" && (
              <TextField
                fullWidth
                label="Maximum Number of Sessions"
                size="small"
                type="number"
                inputProps={{ min: 1 }}
                value={maxSessions}
                placeholder="No limit"
                onChange={(e) => {
                  setMaxSessions(e.target.value);
                  setSessionConfigChanged(true);
                  setHasBasicInfoChanged(true);
                }}
              />
            )}

          <TextField
            fullWidth
            label="Trial Type"
            size="small"
            value={trialDetails.trial_type_title || ""}
            disabled
          />
          <TextField
            fullWidth
            label="Trial Structure"
            size="small"
            value={
              trialDetails.trial_structure === "session_based" ? "Session Based" : "Visit Based"
            }
            disabled
          />

          {role !== USER_ROLES.INVESTIGATOR &&
            role !== USER_ROLES.CRC &&
            role !== USER_ROLES.REVIEWER && (
              <TextField
                select
                fullWidth
                label="Patient UI Version"
                name="ui_version"
                size="small"
                value={trialDetails.ui_version || "v1"}
                onChange={handleFieldChange}
                disabled={isFinalized || isSessionBased}
                helperText={
                  isSessionBased
                    ? "V2 is only available for visit based trials."
                    : "Design of the dashboard patient page and the mobile app for this trial."
                }
              >
                <MenuItem value="v1">V1</MenuItem>
                <MenuItem value="v2">V2</MenuItem>
              </TextField>
            )}

          {role !== USER_ROLES.INVESTIGATOR &&
            role !== USER_ROLES.CRC &&
            role !== USER_ROLES.REVIEWER && (
              <Autocomplete
                fullWidth
                options={scoreEcrfOptions}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={
                  scoreEcrfOptions.find((option) => option.id === trialDetails.score_ecrf) || null
                }
                onChange={(event, selected) => {
                  setHasBasicInfoChanged(true);
                  setTrialDetails((trial) => ({ ...trial, score_ecrf: selected?.id ?? null }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    inputProps={{ ...params.inputProps, "data-testid": "score-ecrf-input" }}
                    label="Score Ecrf"
                    size="small"
                    helperText="eCRF used for manual image scoring. Leave empty to disable manual scoring."
                  />
                )}
              />
            )}

          {role !== USER_ROLES.INVESTIGATOR &&
            role !== USER_ROLES.CRC &&
            role !== USER_ROLES.REVIEWER && (
              <Autocomplete
                fullWidth
                options={scoreEcrfOptions}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={
                  scoreEcrfOptions.find(
                    (option) => option.id === trialDetails.external_score_ecrf
                  ) || null
                }
                onChange={(event, selected) => {
                  setHasBasicInfoChanged(true);
                  setTrialDetails((trial) => ({
                    ...trial,
                    external_score_ecrf: selected?.id ?? null,
                  }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    inputProps={{
                      ...params.inputProps,
                      "data-testid": "external-score-ecrf-input",
                    }}
                    label="External Score Ecrf"
                    size="small"
                    helperText="eCRF used for manual scoring of external photos. Leave empty to disable manual scoring."
                  />
                )}
              />
            )}

          {(role === "CRO Admin" || role === "Cro_coordinator" || role === "Data Manager") && (
            <CustomDatePicker
              label="Actual Study Start Date"
              mode="date"
              variant="outlined"
              value={trialDetails.actual_study_start_date || ""}
              onChange={(val) => {
                setTrialDetails((prev) => ({ ...prev, actual_study_start_date: val }));
                setHasBasicInfoChanged(true);
              }}
            />
          )}

          <Box sx={{ gridColumn: "1 / -1" }}>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              App Options
            </Typography>
            <Autocomplete
              multiple
              id="trial-options-app"
              disabled={isFinalized}
              value={selectedOptions.filter((o) => APP_RELATED_OPTIONS.includes(o.value))}
              onChange={(event, selected, reason, details) =>
                handleChange(event, selected, reason, details, APP_RELATED_OPTIONS)
              }
              options={appOptions}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              getOptionDisabled={(option) => option.isDisabled}
              disableClearable
              renderOption={(props, option) => (
                <li {...props} key={option.value}>
                  <Box>
                    <Typography variant="body2">{option.label}</Typography>
                    {option.description && (
                      <Typography variant="caption" color="text.secondary" component="div">
                        {option.description}
                      </Typography>
                    )}
                  </Box>
                </li>
              )}
              renderInput={(params) => <TextField {...params} size="small" />}
            />
          </Box>

          {!isSessionBasedTrial && (
            <Box sx={{ gridColumn: "1 / -1" }}>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                Dashboard Options
              </Typography>
              <Autocomplete
                multiple
                id="trial-options-dashboard"
                disabled={isFinalized}
                value={selectedOptions.filter((o) => !APP_RELATED_OPTIONS.includes(o.value))}
                onChange={(event, selected, reason, details) =>
                  handleChange(event, selected, reason, details, allDashboardOptionValues)
                }
                options={dashboardOptions}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                getOptionDisabled={(option) => option.isDisabled}
                disableClearable
                renderOption={(props, option) => (
                  <li {...props} key={option.value}>
                    <Box>
                      <Typography variant="body2">{option.label}</Typography>
                      {option.description && (
                        <Typography variant="caption" color="text.secondary" component="div">
                          {option.description}
                        </Typography>
                      )}
                    </Box>
                  </li>
                )}
                renderInput={(params) => <TextField {...params} size="small" />}
              />
            </Box>
          )}
        </Box>

        {(role === "CRO Admin" ||
          role === "Cro_coordinator" ||
          role === "Data Manager" ||
          (role === "Super Admin" && role !== "Inv_coordinator")) && (
          <>
            {trialDetails?.is_setting && trialDetails.trial_structure !== "session_based" && (
              <Box sx={{ gridColumn: "1 / -1" }}>
                <TrialParticipantIdAccordion
                  trialSetting={trialSetting}
                  trialDetails={trialDetails}
                  setChangeTrialSetting={setChangeTrialSetting}
                  changeTrialSetting={changeTrialSetting}
                  setTrialDetails={setTrialDetails}
                  setSiteIdManual={setSiteIdManual}
                  infoSettingChanged={infoSettingChanged}
                  setInfoSettingChanged={setInfoSettingChanged}
                  handleBasicInfoUpdate={handleBasicInfoUpdate}
                  ParticipantIdChanged={ParticipantIdChanged}
                  setParticipantIdChanged={setParticipantIdChanged}
                />
              </Box>
            )}

            <Box sx={{ gridColumn: "1 / -1" }}>
              <InvestigatorSiteIdAccordion
                trialDetails={trialDetails}
                setTrialDetails={setTrialDetails}
                allInvestigators={allInvestigators}
                setAllInvestigators={setAllInvestigators}
                onInvestigatorChange={onInvestigatorChange}
                handleBasicInfoUpdate={handleBasicInfoUpdate}
                setChangeTrialSetting={setChangeTrialSetting}
                investigatorChanged={investigatorChanged}
                setInfoSettingChanged={setInfoSettingChanged}
                setInvestigatorChanged={setInvestigatorChanged}
                AllInvestigatorsData={AllInvestigatorsData}
              />
            </Box>

            <Box sx={{ gridColumn: "1 / -1" }}>
              <TrialReviewersAccordion
                role={role}
                trialDetails={trialDetails}
                allReviewers={allReviewers}
                onReviewerChange={onReviewerChange}
                handleBasicInfoUpdate={handleBasicInfoUpdate}
                AllReviewerData={AllReviewerData}
                setTrialDetails={setTrialDetails}
                setAllReviewers={setAllReviewers}
                setReviewerChanged={setReviewerChanged}
                reviewerChanged={reviewerChanged}
                errorMsg={errorMsg}
                seterrorMsg={seterrorMsg}
              />
            </Box>
          </>
        )}

        {trialDetails.is_maximum_photo_types &&
          (role === "CRO Admin" ||
            role === "Cro_coordinator" ||
            role === "Data Manager" ||
            role === "Super Admin") && (
            <Box sx={{ gridColumn: "1 / -1" }}>
              <MaximumPhotoTypesAccordion
                trialDetails={trialDetails}
                setTrialDetails={setTrialDetails}
                handleBasicInfoUpdate={handleBasicInfoUpdate}
                maximumPhototypes={maximumPhototypes}
                setMaximumPhototypes={setMaximumPhototypes}
              />
            </Box>
          )}

        {(trialDetails.show_econsent || trialDetails.show_digital_consent) &&
          (role === "CRO Admin" ||
            role === "Cro_coordinator" ||
            role === "Data Manager" ||
            role === "Super Admin") && (
            <Box sx={{ gridColumn: "1 / -1" }}>
              <EConsentAccordion
                trialDetails={trialDetails}
                setTrialDetails={setTrialDetails}
                hasBasicInfoChanged={hasBasicInfoChanged}
                setHasBasicInfoChanged={setHasBasicInfoChanged}
                handleBasicInfoUpdate={handleBasicInfoUpdate}
              />
            </Box>
          )}

        {(role === "CRO Admin" ||
          role === "Cro_coordinator" ||
          role === "Data Manager" ||
          role === "Super Admin") && (
          <Box sx={{ gridColumn: "1 / -1" }}>
            <AppActivitiesOrderAccordian
              trialDetails={trialDetails}
              setTrialDetails={setTrialDetails}
              hasBasicInfoChanged={hasBasicInfoChanged}
              setHasBasicInfoChanged={setHasBasicInfoChanged}
              handleBasicInfoUpdate={handleBasicInfoUpdate}
            />
          </Box>
        )}

        {trialDetails.is_maximum_video_types &&
          (role === "CRO Admin" ||
            role === "Cro_coordinator" ||
            role === "Data Manager" ||
            role === "Super Admin") && (
            <>
              <Box sx={{ gridColumn: "1 / -1" }}>
                <MaximumVideoTypesAccordion
                  trialDetails={trialDetails}
                  setTrialDetails={setTrialDetails}
                  handleBasicInfoUpdate={handleBasicInfoUpdate}
                  maximumVideotypes={maximumVideotypes}
                  setMaximumVideotypes={setMaximumVideotypes}
                />
              </Box>

              <Box sx={{ gridColumn: "1 / -1" }}>
                <VideoLengthAccordion
                  trialDetails={trialDetails}
                  setTrialDetails={setTrialDetails}
                  handleBasicInfoUpdate={handleBasicInfoUpdate}
                  videoLength={videoLength}
                  setVideoLength={setVideoLength}
                />
              </Box>
            </>
          )}
      </Box>
    </>
  );
};

export default BasicInfo;
